import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import useDispatch from "@/Components/Utils/useDispatch";
import { cache, DefaultErrorMessage } from "@/Contracts";
import ZMessage from "@molecules/ZMessage";
import DownloadDocumentModal from "@/Components/DocumentsPage/Components/DownloadDocumentModal";
import DocumentsActionCreators from "@/Actions/Documents";
import { DownloadDocumentProductListRequest } from "@/Contracts/Documents";
import { GlobalState } from "@/Reducers/interface";
import {
	downloadWonDocument as downloadDocumentUsingURL,
	downloadWonDocument
} from "@/Services/Documents";
import { CachePDFUrl, DownloadHook } from "./interface";
import { cacheConstant } from "@/Contracts/appCache";
import { useFeatureFlag } from "@/Hooks";
import moment from "moment";
import SettingsActionCreators from "@/Actions/Settings";

const useDownload = (): DownloadHook => {
	const { currentActiveDocument, downloadDocumentModalOpen, fetchingPDF } =
		useSelector((state: GlobalState) => ({
			currentActiveDocument: state.documents.currentActiveDocument,
			downloadDocumentModalOpen:
				state.documents.downloadDocumentModalOpen,
			fetchingPDF: state.settings.isFetchingPDF
		}));

	const [pdfUrl, setPdfUrl] = useState<string>();
	const enablePdfCacheFlag = useFeatureFlag("enable_cache_pdf_url");
	const dispatch = useDispatch();

	useEffect(() => {
		if (
			pdfUrl &&
			currentActiveDocument &&
			fetchingPDF &&
			enablePdfCacheFlag
		) {
			savePDFUrlInCache(pdfUrl);
		}

		return () => {
			setPdfUrl(undefined);
		};
	}, [pdfUrl, currentActiveDocument, fetchingPDF, enablePdfCacheFlag]);

	const savePDFUrlInCache = (pdfUrl: string) => {
		console.info("PDF: saving to cache");

		const urlData: CachePDFUrl = {
			url: pdfUrl,
			documentId: currentActiveDocument?.id,
			lastUpdated: currentActiveDocument?.updated
		};
		cache.save(cacheConstant.pdfUrl, urlData);
	};

	const getPDFUrlFromCache = () => {
		if (!enablePdfCacheFlag) return;

		const urlData: CachePDFUrl | undefined = cache.get(
			cacheConstant.pdfUrl,
			true
		);

		if (!!!urlData?.url.length) {
			return undefined;
		}

		const now = moment();
		const searchParams = new URLSearchParams(urlData?.url);
		const expiryDate = searchParams.get("X-Amz-Date");
		const expirySeconds = searchParams.get("X-Amz-Expires");

		const expiry = moment(expiryDate).add(expirySeconds, "seconds");
		const isURLExpired = now.isAfter(expiry);

		if (
			isURLExpired ||
			urlData.lastUpdated !== currentActiveDocument?.updated
		) {
			invalidatePDFUrl();
			return undefined;
		}

		if (urlData?.documentId === currentActiveDocument?.id) {
			console.info(`PDF: retrieving cache ${urlData.documentId}`);
			return urlData.url;
		}

		return undefined;
	};

	const invalidatePDFUrl = () => {
		const pdfCache: CachePDFUrl | undefined = cache.get(
			cacheConstant.pdfUrl,
			true
		);
		if (!!!pdfCache?.url.length) {
			return;
		}

		console.info(`PDF: invalidating cache ${pdfCache.documentId}`);
		cache.deleteItem(cacheConstant.pdfUrl);
	};

	const downloadDocument = async () => {
		if (!currentActiveDocument || !currentActiveDocument.id) {
			ZMessage.error("No active document");
			return;
		}
		dispatch(DocumentsActionCreators.showDocumentsLoader());
		dispatch(DocumentsActionCreators.downloadDocumentModalOpened());
		try {
			const { url } = await downloadDocumentUsingURL(
				currentActiveDocument.id
			);
			window.open(url);
		} catch (error) {
			console.error(error);
		} finally {
			dispatch(DocumentsActionCreators.hideDocumentsLoader());
			dispatch(DocumentsActionCreators.downloadDocumentModalClosed());
		}
	};

	const downloadDocumentProductList = async () => {
		if (!currentActiveDocument?.revisions?.[0].revision_id) {
			ZMessage.error(DefaultErrorMessage);
			return;
		}
		dispatch(DocumentsActionCreators.downloadDocumentModalOpened());
		try {
			const downloadedFileName =
				currentActiveDocument.name.length > 0
					? currentActiveDocument.name
					: "Document Product List";
			const documentId = currentActiveDocument.id;
			const revisionId = currentActiveDocument.revisions[0].revision_id;

			const downloadDocumentProductListRequest: DownloadDocumentProductListRequest =
				{
					downloadedFileName,
					documentId,
					revisionId
				};

			await dispatch(
				DocumentsActionCreators.downloadDocumentProductList(
					downloadDocumentProductListRequest
				)
			);
		} catch (error) {
			console.error(error);
		} finally {
			dispatch(DocumentsActionCreators.downloadDocumentModalClosed());
		}
	};

	const fetchDocumentPdfUrl = async (forceUpdate?: boolean) => {
		dispatch(SettingsActionCreators.fetchingPdf());
		try {
			if (!currentActiveDocument) {
				ZMessage.error("No active document");
				return;
			} else if (currentActiveDocument.is_deleted) {
				return;
			}

			const cachedPdfUrl = getPDFUrlFromCache();
			if (cachedPdfUrl && !forceUpdate) {
				return cachedPdfUrl;
			}

			const response = await downloadWonDocument(
				currentActiveDocument.id
			);

			setPdfUrl(response.url);
			return response.url;
		} catch (error) {
			console.error(error);
			if (
				error?.code === 504 &&
				error?.displayMessage?.includes("PDF TIMEOUT")
			) {
				ZMessage.error(
					"Document embedding not successful. Please ensure the total number of pages in the document does not exceed 25."
				);
			}
		} finally {
			dispatch(SettingsActionCreators.resetFetchingPdf());
		}

		return undefined;
	};

	const DownloadDocument = (
		<DownloadDocumentModal visible={downloadDocumentModalOpen} />
	);

	return {
		downloadDocument,
		fetchDocumentPdfUrl,
		downloadDocumentProductList,
		DownloadDocument,
		invalidatePDFUrl
	};
};

export default useDownload;
